import {
  IonButton,
  IonContent,
  IonFab,
  IonFabButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonPage,
  IonSpinner,
  useIonToast,
} from "@ionic/react";
import PageHeader from "../components/PageHeader";
import { useParams } from "react-router";
import { decodeParam, encodeParam } from "../util/ApiHelper";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  HierarchyActionResultType,
  HierarchyDataNode,
  HierarchyPartType,
} from "../models/equipment/Equipment";
import { getHierarchy } from "../api/EqupimentHierarchies";
import { TranslationsContext } from "../util/Translations";
import "./EquipmentHierarchy.css";
import {
  appsSharp,
  barcodeOutline,
  chevronDown,
  chevronForward,
  cogOutline,
  cubeOutline,
  listOutline,
  pricetagOutline,
} from "ionicons/icons";
import EquipmentHierarchyPartActionBar from "./EquipmentHierarchyPartActionBar";
import {
  getUserRequesterAccess,
  getUserTechnicianAccess,
} from "../util/UserHelper";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "../util/AppInsights";

/** One node at one position in the tree, flattened for rendering. */
type VisibleRow = {
  node: HierarchyDataNode;
  path: string;
  level: number;
};

function getIcon(partType: number) {
  switch (partType) {
    case 1:
      return cogOutline;
    case 2:
      return barcodeOutline;
    case 3:
      return listOutline;
    case 4:
      return pricetagOutline;
    case 5:
      return cubeOutline;
    default:
      return cogOutline;
  }
}

const trimmed = (value?: string) => (value ?? "").trim();

const normalise = (node: HierarchyDataNode): HierarchyDataNode => ({
  ...node,
  Part: trimmed(node.Part),
  Description: trimmed(node.Description),
  Parent: trimmed(node.Parent),
});

/**
 * Identity for one *position* in the tree.
 *
 * A part can legitimately sit under many parents (eq_hierarchy is a DAG, not a
 * tree) and the same child can even be returned twice for one parent, so a node
 * is identified by its path plus its index among its siblings — never by part
 * alone. That keeps expansion state per-position and gives React a stable,
 * genuinely unique key.
 */
const childPath = (
  parentPath: string,
  node: HierarchyDataNode,
  index: number
) => `${parentPath}>${index}:${node.Part}|${node.PartType}`;

type HierarchyRowProps = {
  row: VisibleRow;
  displayEquipment: string;
  expanded: boolean;
  loading: boolean;
  selected: boolean;
  onToggle: (row: VisibleRow) => void;
  onSelect: (row: VisibleRow) => void;
};

const HierarchyRow: React.FC<HierarchyRowProps> = ({
  row,
  displayEquipment,
  expanded,
  loading,
  selected,
  onToggle,
  onSelect,
}) => {
  const { translations } = useContext(TranslationsContext);
  const { node, level } = row;
  const description = trimmed(node.Description);
  const label = description ? `${node.Part} (${description})` : node.Part;
  const link = `/Equipment/${encodeParam(node.Part)}/hierarchy`;
  const detailsLink = `/Equipment/${encodeParam(node.Part)}`;
  const showsQuantity =
    node.PartType === HierarchyPartType.Catalog ||
    node.PartType === HierarchyPartType.Manufacturer ||
    node.PartType === HierarchyPartType.Item;

  return (
    <IonItem
      button={node.ChildrenExists}
      onClick={() => {
        onSelect(row);
        onToggle(row);
      }}
      lines="none" // Removes extra divider lines for a cleaner look
      style={{
        paddingLeft: `${level * 24}px`, // Even tighter indentation
        minHeight: "18px", // Minimize row height
        "--padding-start": "4px", // Reduce left padding
        "--inner-padding-end": "0px", // Reduce right padding
        "--background": selected ? "var(--ion-color-light)" : "transparent",
      }}
    >
      {/* Expand/Collapse Icon. ChildrenExists comes from the server, so we know
          whether a node has children without fetching its level first. */}
      {loading ? (
        <IonSpinner
          slot="start"
          name="dots"
          style={{ width: "18px", height: "18px", marginRight: "4px" }}
        />
      ) : node.ChildrenExists ? (
        <IonIcon
          slot="start"
          icon={expanded ? chevronDown : chevronForward}
          style={{ fontSize: "18px", marginRight: "4px" }}
        />
      ) : (
        <div style={{ width: "18px" }} /> // Alignment placeholder
      )}
      <div style={{ minHeight: "18px", minWidth: "18px" }}>
        {/* Custom Middle Icon */}
        <IonIcon
          icon={getIcon(node.PartType)}
          style={{ fontSize: "18px", marginRight: "5px", color: "#555" }}
        />
      </div>

      {/* Node */}
      {node.PartType === HierarchyPartType.Equipment && (
        <>
          <IonButton
            fill="clear"
            routerLink={node.Part !== displayEquipment ? link : undefined}
            style={{ maxWidth: "70%" }}
          >
            <IonLabel class="hierarchy-label">{label}</IonLabel>
          </IonButton>
          {" - "}
          <IonButton routerLink={detailsLink} fill="clear">
            {`${translations["lbl_btn_details"] || "Details"}`}
          </IonButton>
        </>
      )}
      {node.PartType !== HierarchyPartType.Equipment &&
        (showsQuantity ? (
          <IonLabel>
            {label} {` (${node.QuantityUsed})`}
          </IonLabel>
        ) : (
          <IonLabel>{label}</IonLabel>
        ))}
    </IonItem>
  );
};

const EquipmentHierarchy: React.FC = () => {
  const { translations } = useContext(TranslationsContext);
  const { equipmentId } = useParams<{ equipmentId: string }>();
  const displayEquipment = decodeParam(equipmentId);
  const [present] = useIonToast();

  const [rootNode, setRootNode] = useState<HierarchyDataNode>();
  const [childrenByPath, setChildrenByPath] = useState<
    Record<string, HierarchyDataNode[]>
  >({});
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const [loadingPaths, setLoadingPaths] = useState<Set<string>>(new Set());
  const [showLoading, setShowLoading] = useState(false);
  const [selected, setSelected] = useState<VisibleRow>();
  const [isRequester, setIsRequester] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const rootPath = rootNode ? `${rootNode.Part}|${rootNode.PartType}` : "";

  const showError = useCallback(
    (result?: HierarchyActionResultType) => {
      const text =
        result?.MessageList?.map((message) => message.Text).join(" ") ||
        translations["msg_hierarchy_load_failed"] ||
        "Unable to load the equipment hierarchy.";
      present({ message: text, color: "danger", duration: 5000 });
    },
    [present, translations]
  );

  /** Fetches a single level: the node itself plus its immediate children. */
  const fetchLevel = useCallback(
    async (part: string, partType: number) => {
      const response = await getHierarchy({
        parent: part,
        parentType: partType,
      });
      const result = response.data as HierarchyActionResultType;
      if (!result?.Success || !result.RootNode) {
        showError(result);
        return undefined;
      }
      return {
        node: normalise(result.RootNode),
        children: (result.RootNode.children ?? []).map(normalise),
      };
    },
    [showError]
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setShowLoading(true);
      setRootNode(undefined);
      setChildrenByPath({});
      setExpandedPaths(new Set());
      setSelected(undefined);
      try {
        const level = await fetchLevel(
          displayEquipment,
          HierarchyPartType.Equipment
        );
        if (cancelled || !level) {
          return;
        }
        const path = `${level.node.Part}|${level.node.PartType}`;
        setRootNode(level.node);
        setChildrenByPath({ [path]: level.children });
        setExpandedPaths(new Set([path]));

        const [technician, requester] = await Promise.all([
          getUserTechnicianAccess(),
          getUserRequesterAccess(),
        ]);
        if (!cancelled) {
          setIsRequester(!technician && requester);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load equipment hierarchy", error);
          showError();
        }
      } finally {
        // Must run on every path — otherwise a failed load leaves the page
        // spinning forever with no way out.
        if (!cancelled) {
          setShowLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [displayEquipment, fetchLevel, showError]);

  const toggle = useCallback(
    async (row: VisibleRow) => {
      const { node, path } = row;
      if (!node.ChildrenExists) {
        return;
      }

      if (expandedPaths.has(path)) {
        setExpandedPaths((previous) => {
          const next = new Set(previous);
          next.delete(path);
          return next;
        });
        return;
      }

      // Already fetched for this position — just reveal it again.
      if (childrenByPath[path]) {
        setExpandedPaths((previous) => new Set(previous).add(path));
        return;
      }

      setLoadingPaths((previous) => new Set(previous).add(path));
      try {
        const level = await fetchLevel(node.Part, node.PartType);
        if (!level) {
          return;
        }
        setChildrenByPath((previous) => ({
          ...previous,
          [path]: level.children,
        }));
        setExpandedPaths((previous) => new Set(previous).add(path));
      } catch (error) {
        console.error("Failed to expand hierarchy node", node.Part, error);
        showError();
      } finally {
        setLoadingPaths((previous) => {
          const next = new Set(previous);
          next.delete(path);
          return next;
        });
      }
    },
    [childrenByPath, expandedPaths, fetchLevel, showError]
  );

  /**
   * Flattens only what is currently expanded. Nothing is materialised until the
   * user asks for it, so the cost of this screen is bounded by what is on
   * screen rather than by the size of the plant's hierarchy.
   */
  const visibleRows = useMemo(() => {
    const rows: VisibleRow[] = [];
    if (!rootNode) {
      return rows;
    }
    const walk = (node: HierarchyDataNode, path: string, level: number) => {
      rows.push({ node, path, level });
      if (!expandedPaths.has(path)) {
        return;
      }
      (childrenByPath[path] ?? []).forEach((child, index) => {
        walk(child, childPath(path, child, index), level + 1);
      });
    };
    walk(rootNode, rootPath, 0);
    return rows;
  }, [rootNode, rootPath, childrenByPath, expandedPaths]);

  //actions are available only for Equipment (part_type = 1) & Inventory (part_type = 5)
  const showActions = !!(
    selected &&
    (selected.node.PartType === HierarchyPartType.Equipment ||
      selected.node.PartType === HierarchyPartType.Item)
  );

  return (
    <IonPage>
      <IonLoading isOpen={showLoading} />
      <PageHeader title={displayEquipment} showBack />
      <IonContent class="ion-padding">
        <h3>{`${translations["lbl_hierarchy"] || "Hierarchy"}`}</h3>
        <IonList style={{ padding: "0" }}>
          {visibleRows.map((row) => (
            <HierarchyRow
              key={row.path}
              row={row}
              displayEquipment={displayEquipment}
              expanded={expandedPaths.has(row.path)}
              loading={loadingPaths.has(row.path)}
              selected={selected?.path === row.path}
              onToggle={toggle}
              onSelect={setSelected}
            />
          ))}
        </IonList>
        {/* Floating Action Button (FAB) - Visible only when a node is selected */}
        {showActions && selected && (
          <>
            <IonFab slot="fixed" vertical="bottom" horizontal="end">
              <IonFabButton onClick={() => setIsOpen(true)}>
                <IonIcon icon={appsSharp}></IonIcon>
              </IonFabButton>
            </IonFab>
            <EquipmentHierarchyPartActionBar
              isOpen={isOpen}
              onDismiss={() => setIsOpen(false)}
              partType={selected.node.PartType}
              isRequester={isRequester}
              equipmentId={selected.node.Part}
              part={selected.node.Part}
            />
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default withAITracking(
  reactPlugin,
  EquipmentHierarchy,
  "EquipmentHierarchy"
);
